/**
 * SecurityEnginePage — admin dashboard for the Security AI Engine.
 *
 * Read-only view of findings + daily report + decision controls for
 * sysadmin/admin. Talks to the IPC channels in routes/security_engine.js:
 *   security:summary
 *   security:list-findings
 *   security:get-finding
 *   security:get-report
 *   security:list-reports
 *   security:mark-accepted-risk
 *   security:mark-false-positive
 *   security:request-remediation-pr
 *   security:generate-report-now
 *   security:trigger-scan
 *
 * Permission gate: security:read (view), security:decide (decision
 * buttons), security:scan (trigger scan button). Server-side enforces
 * — the UI hides buttons the user can't use, but never trusts the
 * client-side hide.
 *
 * Styling: matches the existing plain CSS convention in App.css. No
 * Tailwind, no styled-components.
 *
 * See docs/security_ai_engine_design_2026-06-07.md.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavStep } from "../lib/NavHistory";

const SEVERITY_ORDER = ["critical", "high", "medium", "low", "info"];
const SEVERITY_COLORS = {
  critical: "#b00020",
  high: "#d35400",
  medium: "#b7950b",
  low: "#5a6478",
  info: "#9aa4b2",
};
const SOURCE_LABELS = {
  npm_audit: "NPM",
  trivy: "Image (ECR)",
  aws_config: "AWS Config",
  codebase_regex: "Code",
  ai_diff_review: "PR Review",
  behavior_anomaly: "Behavior",
};
const REMEDIATION_LABELS = {
  pending: "Pending review",
  pr_open: "PR open",
  pr_merged: "Merged",
  accepted_risk: "Risk accepted",
  false_positive: "False positive",
};

export default function SecurityEnginePage({ user }) {
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    severities: [], sources: [], remediation_statuses: [], only_exploitable: false,
  });
  const [activeFinding, setActiveFinding] = useState(null);  // detail panel
  const [busy, setBusy] = useState(null);                    // 'mark-accepted'/'mark-false'/etc.

  // #65 Phase C2b-1: the finding detail (right pane) is this page's master->
  // detail sub-view. Register ONE Back step so global Back closes the detail and
  // returns to the findings list/report state before navigating away — same
  // handler the in-view detail close (onClose) uses. Unconditional, top-level
  // (no early returns precede it). LITERAL label — never a finding title/CVE/id
  // (no PHI/data in the sessionStorage-backed nav stack).
  useNavStep({
    active: !!activeFinding,
    label: 'Finding',
    onBack: () => setActiveFinding(null),
  });
  const [actionMessage, setActionMessage] = useState(null);
  const [actionQueue, setActionQueue] = useState([]);        // Phase 2 pending automation

  const role = (user && user.role) || "other";
  // RBAC spread remediation: permission-key the decide action so the UI tracks
  // the actual grant (admin + sysadmin hold security:decide today — unchanged),
  // rather than a hard-coded role list that could drift. canScan stays
  // deliberately sysadmin-only (a narrower UX choice than the security:scan grant).
  const perms = new Set((user && user.permissions) || []);
  const canDecide = perms.has("security:decide");
  const canScan = role === "sysadmin";

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = window.zignagenixAPI;
      const [s, f, r, q] = await Promise.all([
        api.invoke("security:summary").catch(() => null),
        api.invoke("security:list-findings", filters).catch(() => []),
        api.invoke("security:get-report", {}).catch(() => null),
        api.invoke("security:list-action-queue", { limit: 25 }).catch(() => []),
      ]);
      setSummary(s);
      setFindings(Array.isArray(f) ? f : []);
      setReport(r && !r.error ? r : null);
      setActionQueue(Array.isArray(q) ? q : []);
      setLoading(false);
    } catch (e) {
      setError((e && e.message) || "Failed to load security data");
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const counts = useMemo(() => {
    if (summary) return summary;
    const out = { critical_open: 0, high_open: 0, medium_open: 0, ai_exploitable_total: 0, total: findings.length };
    for (const f of findings) {
      const sev = f.ai_severity || f.base_severity;
      const open = !["accepted_risk", "false_positive", "pr_merged"].includes(f.remediation_status);
      if (open && sev === "critical") out.critical_open++;
      if (open && sev === "high") out.high_open++;
      if (open && sev === "medium") out.medium_open++;
      if (f.ai_exploitable === true) out.ai_exploitable_total++;
    }
    return out;
  }, [summary, findings]);

  const filteredFindings = findings;  // server already filtered

  async function openFinding(id) {
    setActiveFinding({ loading: true, id });
    try {
      const r = await window.zignagenixAPI.invoke("security:get-finding", { id });
      if (r && r.error) {
        setActiveFinding({ error: r.error });
      } else {
        setActiveFinding(r);
      }
    } catch (e) {
      setActiveFinding({ error: (e && e.message) || "Failed to load finding" });
    }
  }

  async function decide(channel, payload, successMsg) {
    setBusy(channel);
    setActionMessage(null);
    try {
      const r = await window.zignagenixAPI.invoke(channel, payload);
      if (r && r.error) {
        // Server returns a code in `error` and a human explanation in `message`.
        setActionMessage({ kind: "error", text: (r.message || r.error) });
      } else {
        // Prefer the server's own note/message (e.g. "queued", "already queued").
        setActionMessage({ kind: "ok", text: (r && (r.note || r.message)) || successMsg });
        await loadAll();
        if (activeFinding && activeFinding.finding) {
          await openFinding(activeFinding.finding.id);
        }
      }
    } catch (e) {
      setActionMessage({ kind: "error", text: (e && e.message) || "Action failed" });
    }
    setBusy(null);
  }

  async function markAccepted(id) {
    const notes = window.prompt("Why are we accepting this risk? (audit log requires ≥10 chars)");
    if (!notes || notes.trim().length < 10) {
      setActionMessage({ kind: "error", text: "Notes ≥10 chars are required for audit" });
      return;
    }
    await decide("security:mark-accepted-risk", { id, notes: notes.trim() }, "Marked accepted risk");
  }

  async function markFalsePositive(id) {
    const notes = window.prompt("Why is this a false positive? (audit log requires ≥10 chars)");
    if (!notes || notes.trim().length < 10) {
      setActionMessage({ kind: "error", text: "Notes ≥10 chars are required for audit" });
      return;
    }
    await decide("security:mark-false-positive", { id, notes: notes.trim() }, "Marked false positive");
  }

  async function requestPR(id) {
    if (!window.confirm("Queue an automated dependency-bump PR for this finding? The security worker opens it (within ~15 min) for your review — it is never auto-merged. Only npm findings with a non-major fix can be auto-PR'd.")) return;
    await decide("security:request-remediation-pr", { id }, "Remediation PR queued");
  }

  async function regenerateReport() {
    await decide("security:generate-report-now", {}, "Report regenerated");
  }

  async function triggerScan() {
    if (!window.confirm("Queue an on-demand security scan? The worker dispatches it (within ~15 min); findings and the daily report refresh when it finishes.")) return;
    await decide("security:trigger-scan", {}, "Scan queued");
  }

  return (
    <div className="security-engine-page" style={{ padding: "16px 24px", maxWidth: 1400, margin: "0 auto" }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Security AI Engine</h1>
          <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>
            Daily scans with Bedrock-powered AI triage. Human-in-the-loop remediation.{" "}
            <span style={{ color: "#9aa4b2" }}>HIPAA · GDPR · Mexico LFPDPPP Art. 19</span>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={regenerateReport} disabled={!canDecide || busy} style={btnSecondary}>Regenerate report</button>
          {canScan && (
            <button onClick={triggerScan} disabled={busy} style={btnPrimary}>Trigger scan now</button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fde8e8", border: "1px solid #f5a5a5", padding: 10, borderRadius: 4, marginBottom: 12, color: "#8b0000" }}>
          {error}
        </div>
      )}
      {actionMessage && (
        <div style={{
          background: actionMessage.kind === "ok" ? "#e9f5e9" : "#fde8e8",
          border: actionMessage.kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
          padding: 10, borderRadius: 4, marginBottom: 12,
          color: actionMessage.kind === "ok" ? "#2d6b2d" : "#8b0000",
        }}>{actionMessage.text}</div>
      )}

      {/* ── Summary cards ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <SummaryCard label="Critical (open)" value={counts.critical_open} color={SEVERITY_COLORS.critical} />
        <SummaryCard label="High (open)" value={counts.high_open} color={SEVERITY_COLORS.high} />
        <SummaryCard label="Medium (open)" value={counts.medium_open} color={SEVERITY_COLORS.medium} />
        <SummaryCard label="AI-exploitable" value={counts.ai_exploitable_total} color="#1f6feb" />
      </div>

      {/* ── Pending automation (Phase 2) ────────────────────────── */}
      <PendingAutomation queue={actionQueue} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>

        {/* ── Left: findings list ───────────────────────────────── */}
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: 15, margin: 0, marginRight: 8 }}>Findings ({filteredFindings.length})</h2>
            <FilterChip
              label="exploitable only"
              active={filters.only_exploitable}
              onClick={() => setFilters({ ...filters, only_exploitable: !filters.only_exploitable })}
            />
            <FilterChip
              label="critical"
              active={filters.severities.includes("critical")}
              onClick={() => toggleArrayFilter("severities", "critical", filters, setFilters)}
            />
            <FilterChip
              label="high"
              active={filters.severities.includes("high")}
              onClick={() => toggleArrayFilter("severities", "high", filters, setFilters)}
            />
            <FilterChip
              label="hide accepted"
              active={!filters.remediation_statuses.includes("accepted_risk")}
              onClick={() => {
                const has = filters.remediation_statuses.includes("accepted_risk");
                const next = has
                  ? filters.remediation_statuses.filter((s) => s !== "accepted_risk" && s !== "false_positive")
                  : ["pending", "pr_open", "pr_merged"];
                setFilters({ ...filters, remediation_statuses: next });
              }}
            />
          </div>

          {loading ? (
            <div style={{ color: "#9aa4b2", padding: 12 }}>Loading…</div>
          ) : filteredFindings.length === 0 ? (
            <div style={{ color: "#9aa4b2", padding: 12 }}>No findings match the current filter.</div>
          ) : (
            <div style={{ border: "1px solid #e1e6ed", borderRadius: 4, maxHeight: 600, overflowY: "auto" }}>
              {filteredFindings.map((f) => (
                <FindingRow
                  key={f.id}
                  finding={f}
                  active={activeFinding && activeFinding.finding && activeFinding.finding.id === f.id}
                  onClick={() => openFinding(f.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Right: detail / report ────────────────────────────── */}
        <div>
          {activeFinding ? (
            <FindingDetail
              data={activeFinding}
              canDecide={canDecide}
              busy={busy}
              onMarkAccepted={markAccepted}
              onMarkFalsePositive={markFalsePositive}
              onRequestPR={requestPR}
              onClose={() => setActiveFinding(null)}
            />
          ) : report ? (
            <DailyReport report={report} />
          ) : (
            <div style={{ color: "#9aa4b2", padding: 12, border: "1px solid #e1e6ed", borderRadius: 4 }}>
              No daily report yet. Trigger the first scan to populate.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────

function PendingAutomation({ queue }) {
  const items = Array.isArray(queue) ? queue : [];
  // Only worth showing if there's anything in-flight or a recent outcome.
  const active = items.filter((q) => q.status === "queued" || q.status === "processing");
  const recentDone = items.filter((q) => q.status === "done" || q.status === "failed").slice(0, 4);
  if (active.length === 0 && recentDone.length === 0) return null;

  const STATUS_STYLE = {
    queued:     { label: "queued",     bg: "#fff7e6", bd: "#f0c36d", fg: "#8a6d1b" },
    processing: { label: "in progress", bg: "#e8f0fb", bd: "#9ec1f0", fg: "#1f4f96" },
    done:       { label: "done",       bg: "#e9f5e9", bd: "#a5d5a5", fg: "#2d6b2d" },
    failed:     { label: "failed",     bg: "#fde8e8", bd: "#f5a5a5", fg: "#8b0000" },
  };

  function describe(q) {
    if (q.action_type === "scan") return "On-demand scan";
    const pkg = q.package_name || (q.payload && q.payload.package) || "dependency";
    const fix = (q.payload && q.payload.fix_version) ? ` → ${q.payload.fix_version}` : "";
    return `Remediation PR · ${pkg}${fix}`;
  }

  return (
    <div style={{ border: "1px solid #e1e6ed", borderRadius: 4, background: "#fff", padding: 12, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#3a4a5e" }}>
        Pending automation {active.length > 0 && <span style={{ color: "#1f6feb" }}>· {active.length} in flight</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {[...active, ...recentDone].map((q) => {
          const st = STATUS_STYLE[q.status] || STATUS_STYLE.queued;
          return (
            <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: st.fg, background: st.bg, border: `1px solid ${st.bd}`, padding: "2px 7px", borderRadius: 3, minWidth: 78, textAlign: "center" }}>
                {st.label}
              </span>
              <span style={{ flex: 1, color: "#1F2937" }}>{describe(q)}</span>
              {q.result_url && (
                <a href={q.result_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#1f6feb" }}>
                  {q.action_type === "scan" ? "view run" : "view PR"}
                </a>
              )}
              {q.status === "failed" && q.result_detail && (
                <span title={q.result_detail} style={{ fontSize: 11, color: "#8b0000", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {q.result_detail}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e1e6ed", borderRadius: 4, padding: 14 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: value > 0 ? color : "#5a6478", marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "4px 10px", fontSize: 12, borderRadius: 12,
        background: active ? "#1f6feb" : "#fff",
        color: active ? "#fff" : "#5a6478",
        border: `1px solid ${active ? "#1f6feb" : "#c9d6e8"}`,
        cursor: "pointer",
      }}
    >{label}</button>
  );
}

function toggleArrayFilter(key, value, filters, setFilters) {
  const arr = filters[key] || [];
  const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  setFilters({ ...filters, [key]: next });
}

function FindingRow({ finding, active, onClick }) {
  const sev = finding.ai_severity || finding.base_severity || "info";
  const color = SEVERITY_COLORS[sev] || "#9aa4b2";
  return (
    <div
      onClick={onClick}
      style={{
        padding: "10px 12px", cursor: "pointer", borderBottom: "1px solid #f0f3f7",
        background: active ? "#f4f7fb" : "transparent",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
          color: "#fff", background: color, padding: "2px 6px", borderRadius: 3,
        }}>{sev}</span>
        <span style={{ fontSize: 11, color: "#5a6478" }}>{SOURCE_LABELS[finding.source] || finding.source}</span>
        {finding.ai_exploitable === true && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#1f6feb",
            background: "#e8f0fb", padding: "1px 6px", borderRadius: 3,
          }}>AI: exploitable</span>
        )}
        <span style={{ fontSize: 11, color: "#9aa4b2", marginLeft: "auto" }}>
          {REMEDIATION_LABELS[finding.remediation_status] || finding.remediation_status}
        </span>
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: "#1f2a3b" }}>
        <strong>{finding.package_name || finding.affected_path || "(unknown)"}</strong>
        {finding.vulnerability_id && <span style={{ color: "#5a6478" }}> · {finding.vulnerability_id}</span>}
      </div>
      {finding.ai_reasoning && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#5a6478", fontStyle: "italic" }}>
          {finding.ai_reasoning.slice(0, 160)}{finding.ai_reasoning.length > 160 ? "…" : ""}
        </div>
      )}
    </div>
  );
}

function FindingDetail({ data, canDecide, busy, onMarkAccepted, onMarkFalsePositive, onRequestPR, onClose }) {
  if (data.loading) return <div style={detailWrap}>Loading…</div>;
  if (data.error) return <div style={detailWrap}>Error: {data.error}</div>;
  if (!data.finding) return null;
  const f = data.finding;
  const sev = f.ai_severity || f.base_severity || "info";
  const color = SEVERITY_COLORS[sev] || "#9aa4b2";
  const isOpen = !["accepted_risk", "false_positive", "pr_merged"].includes(f.remediation_status);

  return (
    <div style={detailWrap}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>Finding detail</h3>
        <button onClick={onClose} style={btnGhost}>Back to report</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
          color: "#fff", background: color, padding: "3px 8px", borderRadius: 3,
        }}>{sev}</span>
        <span style={{ fontSize: 11, color: "#5a6478" }}>{SOURCE_LABELS[f.source] || f.source}</span>
        <span style={{ fontSize: 11, color: "#5a6478", marginLeft: "auto" }}>
          {REMEDIATION_LABELS[f.remediation_status] || f.remediation_status}
        </span>
      </div>

      <div style={{ fontSize: 13, marginBottom: 8 }}>
        <strong>{f.package_name || "(no package)"}</strong>
        {f.vulnerability_id && <span style={{ color: "#5a6478" }}> · {f.vulnerability_id}</span>}
      </div>
      {f.affected_path && (
        <div style={{ fontSize: 12, color: "#5a6478", marginBottom: 8 }}>
          Path: <code>{f.affected_path}</code>
        </div>
      )}
      {f.cvss_score && (
        <div style={{ fontSize: 12, color: "#5a6478", marginBottom: 8 }}>
          Base CVSS: <strong>{f.cvss_score}</strong>
        </div>
      )}

      {f.ai_reasoning && (
        <div style={{ background: "#f4f7fb", border: "1px solid #c9d6e8", padding: 10, borderRadius: 4, marginBottom: 10 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: "#5a6478", letterSpacing: 0.4, marginBottom: 4 }}>
            AI triage{f.ai_model ? ` · ${f.ai_model.split("/").pop()}` : ""}
          </div>
          <div style={{ fontSize: 12, color: "#1f2a3b", marginBottom: 6 }}>
            {f.ai_reasoning}
          </div>
          {f.ai_recommended_action && (
            <div style={{ fontSize: 12 }}>
              <strong>Recommended action:</strong> {f.ai_recommended_action}
            </div>
          )}
        </div>
      )}

      {f.evidence && Object.keys(f.evidence).length > 0 && (
        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 12, color: "#5a6478", cursor: "pointer" }}>Evidence</summary>
          <pre style={{ background: "#f9fafc", padding: 8, fontSize: 11, overflowX: "auto", borderRadius: 3 }}>
            {JSON.stringify(f.evidence, null, 2)}
          </pre>
        </details>
      )}

      {canDecide && isOpen && (
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          <button onClick={() => onRequestPR(f.id)} disabled={!!busy} style={btnPrimary}>Request remediation PR</button>
          <button onClick={() => onMarkAccepted(f.id)} disabled={!!busy} style={btnSecondary}>Accept risk…</button>
          <button onClick={() => onMarkFalsePositive(f.id)} disabled={!!busy} style={btnSecondary}>False positive…</button>
        </div>
      )}

      {data.history && data.history.length > 1 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 12, color: "#5a6478", cursor: "pointer" }}>
            History ({data.history.length} entries)
          </summary>
          <div style={{ marginTop: 6 }}>
            {data.history.map((h) => (
              <div key={h.id} style={{ fontSize: 11, color: "#5a6478", borderTop: "1px solid #f0f3f7", padding: "4px 0" }}>
                {new Date(h.created_at).toLocaleString()} — {h.ai_severity || "untriaged"} / {REMEDIATION_LABELS[h.remediation_status] || h.remediation_status}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function DailyReport({ report }) {
  return (
    <div style={detailWrap}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>Daily report — {report.report_date}</h3>
        <span style={{ fontSize: 11, color: "#9aa4b2" }}>scan {report.scan_id}</span>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 12, fontSize: 12 }}>
        <span><strong>{report.total_findings}</strong> total</span>
        <span style={{ color: SEVERITY_COLORS.critical }}><strong>{report.critical_count}</strong> critical</span>
        <span style={{ color: SEVERITY_COLORS.high }}><strong>{report.high_count}</strong> high</span>
        <span style={{ color: SEVERITY_COLORS.medium }}><strong>{report.medium_count}</strong> medium</span>
        <span style={{ color: "#1f6feb" }}>+{report.new_since_yesterday}</span>
        <span style={{ color: "#2d6b2d" }}>−{report.resolved_since_yesterday}</span>
      </div>
      <div style={{
        fontSize: 13, lineHeight: 1.5, color: "#1f2a3b", whiteSpace: "pre-wrap",
        background: "#fff", border: "1px solid #e1e6ed", padding: 12, borderRadius: 4, maxHeight: 500, overflowY: "auto",
      }}>
        {report.ai_summary_markdown}
      </div>
    </div>
  );
}

// ── Inline styles (matching the plain-CSS convention in App.css) ────────
const btnPrimary = {
  padding: "6px 12px", fontSize: 12, fontWeight: 600,
  background: "#1f6feb", color: "#fff",
  border: "none", borderRadius: 3, cursor: "pointer",
};
const btnSecondary = {
  padding: "6px 12px", fontSize: 12, fontWeight: 600,
  background: "#fff", color: "#3a4a5e",
  border: "1px solid #c9d6e8", borderRadius: 3, cursor: "pointer",
};
const btnGhost = {
  padding: "4px 8px", fontSize: 11,
  background: "transparent", color: "#5a6478",
  border: "1px solid #e1e6ed", borderRadius: 3, cursor: "pointer",
};
const detailWrap = {
  border: "1px solid #e1e6ed", borderRadius: 4, padding: 14, background: "#fff",
};
